"use client"
import {
  HStack,
  Icon,
  Image,
  Link,
  LinkBox,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { type FC, useMemo, useState } from "react"
import useSWR from "swr"

import BlurFade from "@/components/Animations/BlurFade"
import { ExternalLinkIcon, LinkIcon } from "@/components/icons"
import MarkdownParser from "@/components/MarkdownParser"
import { type UrlBlock as UrlBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface URLBlockProps extends ContentBlockTitleNumberProps {
  block: UrlBlockType
}

interface UrlMetadata {
  title?: string
  description?: string
  image?: string
  url?: string
}

type UrlRepeaterItem = string | { url?: string | null } | null

interface UrlPreviewContentProps {
  error: boolean
  loading: boolean
  metadata?: UrlMetadata
  showThumbnail: boolean
  url: string
}

interface UrlPreviewCardProps {
  showThumbnail: boolean
  url: string
}

const fetchUrlMetadata = async (resource: string) => {
  const response = await fetch(resource)

  if (!response.ok) {
    throw new Error("Failed to fetch metadata")
  }

  return response.json() as Promise<UrlMetadata>
}

const isString = (url: string | undefined): url is string => Boolean(url)

const getRepeaterUrl = (item: UrlRepeaterItem) => {
  if (typeof item === "string") {
    return item.trim()
  }

  if (item?.url) {
    return item.url.trim()
  }

  return ""
}

const getBlockUrls = (block: UrlBlockType) => {
  const repeaterUrls = Array.isArray(block.urls)
    ? block.urls.reduce<string[]>((urls, item) => {
        const url = getRepeaterUrl(item)

        if (isString(url)) {
          urls.push(url)
        }

        return urls
      }, [])
    : []
  const fallbackUrl = block.url?.trim()
  const urls = repeaterUrls.length > 0 ? repeaterUrls : [fallbackUrl]

  return Array.from(new Set(urls.filter(isString)))
}

const UrlPreviewContent: FC<UrlPreviewContentProps> = ({
  error = false,
  loading = false,
  metadata = undefined,
  showThumbnail = false,
  url = "",
}) => {
  const [imageError, setImageError] = useState(false)

  const host = useMemo(() => {
    try {
      return new URL(url).hostname
    } catch {
      console.warn("Invalid URL:", url)
      return undefined
    }
  }, [url])

  const favicon = host
    ? `https://www.google.com/s2/favicons?domain=${host}`
    : undefined

  if (metadata?.title) {
    return (
      <LinkBox maxW="full" w="full">
        <Link
          _hover={{ textDecoration: "none" }}
          aria-label={`Visit ${metadata.title}`}
          backgroundSize="0 0"
          borderRadius="lg"
          display="block"
          href={url}
          outlineColor="primary.400"
          outlineOffset="0px"
          outlineWidth="2px"
          p={0}
          rel="noopener noreferrer"
          target="_blank"
          width="100%"
        >
          <Stack
            _hover={{
              borderColor: "primary.500",
            }}
            alignItems="stretch"
            bg={"bg.paper"}
            bgColor="bg.surface"
            borderColor="border"
            borderRadius="lg"
            borderWidth="1px"
            flexDirection={{ base: "column", md: "row" }}
            gap={{ base: 4 }}
            overflow={"hidden"}
            p={{ base: 4 }}
            rounded="md"
            transition="box-shadow 0.2s, border-color 0.2s ease"
            width="100%"
          >
            {metadata.image && showThumbnail && !imageError ? (
              <Image
                alt={metadata.title || "Link preview image"}
                aspectRatio={1.91 / 1}
                borderRadius={{ base: 0, md: "md" }}
                flexShrink={0}
                loading="lazy"
                objectFit="cover"
                onError={() => setImageError(true)}
                src={metadata.image}
                width={{ base: "100%", md: "180px" }}
              />
            ) : showThumbnail && (imageError || !metadata.image) ? (
              <VStack
                alignItems="center"
                aria-label="Link icon placeholder"
                aspectRatio={1.91 / 1}
                bg="bg.muted"
                borderRadius={{ base: 0, md: "md" }}
                flexShrink={0}
                justifyContent="center"
                width={{ base: "100%", md: "180px" }}
              >
                <Icon as={LinkIcon} boxSize={8} color="fg.muted" />
              </VStack>
            ) : null}
            <VStack
              align="start"
              flex={1}
              gap={1}
              overflow="hidden"
              p={{ base: 4, md: 0 }}
            >
              <Text className="url-title-meta" fontWeight="bold" lineClamp={2}>
                {metadata.title}
              </Text>
              {metadata.description && (
                <Text color="fg.muted" fontSize="sm" lineClamp={3}>
                  {metadata.description}
                </Text>
              )}
              <Text
                alignItems="center"
                className="url-x-meta"
                color="fg.subtle"
                fontSize="xs"
                gap="1"
                lineClamp={1}
              >
                <HStack as="span" display="inline-flex" gap="1">
                  {metadata.url || url}
                  <Icon
                    aria-hidden="true"
                    as={ExternalLinkIcon}
                    className="url-icon-meta"
                  />
                </HStack>
              </Text>
            </VStack>
          </Stack>
        </Link>
      </LinkBox>
    )
  }

  return (
    <Link
      _hover={{ textDecoration: "none" }}
      aria-label={`Visit ${host || url}`}
      backgroundSize="0 0"
      display="block"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
      width="100%"
    >
      <Stack
        _hover={{
          borderColor: "primary.500",
        }}
        alignItems="stretch"
        bg={"bg.paper"}
        bgColor="bg.surface"
        borderColor="border"
        borderRadius="lg"
        borderWidth="1px"
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: 0, md: 4 }}
        overflow={"hidden"}
        p={{ base: 0, md: 4 }}
        rounded="md"
        transition="box-shadow 0.2s ease, border-color 0.2s ease"
        width="100%"
      >
        {favicon && (
          <Image
            alt={`${host || "Website"} favicon`}
            borderRadius="md"
            boxSize="40px"
            flexShrink={0}
            loading="lazy"
            objectFit="contain"
            src={favicon}
          />
        )}
        <VStack align="start" flex={1} gap={1} overflow="hidden">
          <Text className="url-title" fontWeight="bold" lineClamp={1}>
            {loading ? (
              <Spinner aria-label="Loading preview" size="xs" />
            ) : (
              host || url
            )}
          </Text>
          {error && (
            <Text color="red.400" fontSize="xs">
              Preview unavailable
            </Text>
          )}
          <Text
            alignItems="center"
            className="url-url"
            color="fg.subtle"
            display="inline-flex"
            fontSize="xs"
            gap="1"
            lineClamp={1}
          >
            {url}{" "}
            <Icon
              aria-hidden="true"
              as={ExternalLinkIcon}
              className="url-icon"
            />
          </Text>
        </VStack>
      </Stack>
    </Link>
  )
}

const UrlPreviewCard: FC<UrlPreviewCardProps> = ({ showThumbnail, url }) => {
  const {
    data: metadata,
    error: metadataError,
    isLoading: loading,
  } = useSWR<UrlMetadata>(
    url ? `/api/url-metadata?url=${encodeURIComponent(url)}` : null,
    fetchUrlMetadata
  )

  return (
    <UrlPreviewContent
      error={!!metadataError}
      loading={loading}
      metadata={metadata}
      showThumbnail={showThumbnail}
      url={url}
    />
  )
}

const UrlBlock: FC<URLBlockProps> = ({ block, titleNumber }) => {
  const urls = getBlockUrls(block)
  const show_thumbnail = block?.show_thumbnail ?? true
  const darker_bg = block?.darker_bg || false
  const two_columns = block?.two_columns_url || false
  const title = block?.title
  const trimmedTitle = title?.trim()
  const description = block?.description
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    description !== undefined && description !== "" && description !== null

  if (!block) return null
  if (urls.length === 0) return null

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="url-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {" "}
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{ base: "100%", lg: two_columns ? "30%" : "100%" }}
          {...getStickyContentBlockLeftProps(two_columns)}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle
              className="url_block_title"
              fontWeight="bold"
              lineHeight="shorter"
              title={trimmedTitle}
              titleNumber={titleNumber}
            />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: two_columns ? "70%" : "100%",
        }}
        pl={!hasTitle && two_columns ? { base: 0, lg: 12 } : 0}
      >
        <BlurFade delay={0} flexDirection="column" gap={4} inView w="full">
          {urls.map((url) => (
            <UrlPreviewCard
              key={url}
              showThumbnail={show_thumbnail}
              url={url}
            />
          ))}
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default UrlBlock
